import type { EnrichedVulnerability } from '@/sections/threats/types';
import type { Risk } from '@/types';

// ---------------------------------------------------------------------------
// SSVC input derivation (mirrors backend pkg/lib/ssvc/ssvc.go)
// These functions are pinned by unit tests against the Go SSVC table
// semantics — do not diverge from ssvc.go.
// ---------------------------------------------------------------------------

export interface SsvcInputs {
  exploitation: string;
  automatable: string;
  technicalImpact: string;
  missionPrevalence: string;
  publicWellBeing: string;
  escalationLevel: number;
  coordinatorDecision: string;
  decision: string;
}

function parseCvssMetrics(vector: string): Record<string, string> {
  const metrics: Record<string, string> = {};
  for (const part of vector.split('/')) {
    const [key, value] = part.split(':');
    if (key && value) metrics[key] = value;
  }
  return metrics;
}

export function deriveExploitation(
  isKev: boolean,
  epssScore: number,
  validated: boolean
): string {
  if (isKev) return 'active';
  if (validated && epssScore >= 0.6) return 'active';
  if (validated || epssScore >= 0.1) return 'poc';
  return 'none';
}

export function deriveAutomatable(cvssVector: string): string {
  const m = parseCvssMetrics(cvssVector);
  return m['AV'] === 'N' && m['AC'] === 'L' ? 'yes' : 'no';
}

export function deriveTechnicalImpact(cvssVector: string): string {
  const m = parseCvssMetrics(cvssVector);
  for (const key of ['C', 'I', 'A', 'VC', 'VI', 'VA']) {
    if (m[key] === 'H') return 'total';
  }
  return 'partial';
}

export function getEscalationLevel(mission: string, wellBeing: string): number {
  const mMap: Record<string, number> = { minimal: 0, support: 1, essential: 2 };
  const wMap: Record<string, number> = {
    minimal: 0,
    material: 1,
    irreversible: 2,
  };
  return Math.max(mMap[mission] ?? 0, wMap[wellBeing] ?? 0);
}

export const DEPLOYER_TABLE: Record<string, Record<number, string>> = {
  Track: { 0: 'Track', 1: 'Track', 2: 'Track*' },
  'Track*': { 0: 'Track*', 1: 'Attend', 2: 'Attend' },
  Attend: { 0: 'Attend', 1: 'Attend', 2: 'Act' },
  Act: { 0: 'Act', 1: 'Act', 2: 'Act' },
};

export function coordinatorDecision(
  exploitation: string,
  automatable: string,
  technicalImpact: string
): string {
  const table: Record<string, string> = {
    'none/no/partial': 'Track',
    'none/no/total': 'Track',
    'none/yes/partial': 'Track*',
    'none/yes/total': 'Attend',
    'poc/no/partial': 'Track*',
    'poc/no/total': 'Attend',
    'poc/yes/partial': 'Attend',
    'poc/yes/total': 'Attend',
    'active/no/partial': 'Attend',
    'active/no/total': 'Act',
    'active/yes/partial': 'Act',
    'active/yes/total': 'Act',
  };
  return table[`${exploitation}/${automatable}/${technicalImpact}`] ?? 'Track';
}

export function deriveSsvcInputs(
  threatIntel: EnrichedVulnerability | undefined,
  risk: Risk,
  missionPrevalence?: string
): SsvcInputs | null {
  if (!threatIntel) return null;

  const isKev = threatIntel.is_kev ?? false;
  const epssScore = threatIntel.epss?.score ?? 0;
  const validated = risk.status?.startsWith('O') ?? false;

  const cvssEntry = threatIntel.cvss?.find(
    c => c.base_vector && c.version !== 'v2'
  );
  const cvssVector = cvssEntry?.base_vector ?? '';

  const exploitation = deriveExploitation(isKev, epssScore, validated);
  const automatable = deriveAutomatable(cvssVector);
  const technicalImpact = deriveTechnicalImpact(cvssVector);
  const publicWellBeing = risk.public_well_being ?? 'minimal';
  const mp = missionPrevalence || 'minimal';
  const escalation = getEscalationLevel(mp, publicWellBeing);
  const base = coordinatorDecision(exploitation, automatable, technicalImpact);
  const decision =
    risk.ssvc_decision ?? DEPLOYER_TABLE[base]?.[escalation] ?? base;

  return {
    exploitation,
    automatable,
    technicalImpact,
    missionPrevalence: mp,
    publicWellBeing,
    escalationLevel: escalation,
    coordinatorDecision: base,
    decision,
  };
}
